import type { Feature, GeoJsonProperties, GeometryObject } from 'geojson';
import type {
  Map,
  GeoJSONFeature,
  MapMouseEvent,
  MapTouchEvent,
  Point,
  PointLike,
  TargetFeature,
} from 'mapbox-gl';
import { feature } from '@turf/helpers';

export class BdMapQuery {
  public static getFeaturesOnClickedPosition<
    TGeoJsonProperties extends GeoJsonProperties = GeoJsonProperties,
  >(
    mapMouseEvent: MapMouseEvent | MapTouchEvent,
    layerName: string,
    bboxSize: number,
  ): Feature<GeometryObject, TGeoJsonProperties>[] {
    return BdMapQuery.getFeaturesOnPosition<TGeoJsonProperties>(
      mapMouseEvent.target,
      mapMouseEvent.point,
      layerName,
      bboxSize,
    );
  }

  public static getFeaturesOnPosition<
    TGeoJsonProperties extends GeoJsonProperties = GeoJsonProperties,
  >(
    map: Map,
    point: Point,
    layerName: string,
    bboxSize = 0,
    zoom?: {
      minZoom?: number;
      maxZoom?: number;
    },
  ): Feature<GeometryObject, TGeoJsonProperties>[] {
    const bbox = [
      [point.x - bboxSize, point.y - bboxSize],
      [point.x + bboxSize, point.y + bboxSize],
    ] as [PointLike, PointLike];

    return map
      .queryRenderedFeatures(bbox, { layers: [layerName] })
      .filter((it: GeoJSONFeature) => {
        if (zoom) {
          const currentMapZoom = (it as TargetFeature)._z;
          if (!zoom.maxZoom) {
            return zoom.minZoom < currentMapZoom;
          }
          if (!zoom.minZoom) {
            return currentMapZoom <= zoom.maxZoom;
          }

          return (
            zoom.minZoom <= currentMapZoom && currentMapZoom < zoom.maxZoom
          );
        } else {
          return true;
        }
      })
      .map((it: GeoJSONFeature) => {
        return feature(it.geometry, it.properties as TGeoJsonProperties, {
          bbox: it.bbox,
          id: it.id,
        });
      });
  }
}
